<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollStore } from '@/store/payroll-data'
import { useUserStore } from '@/store/user'
import {
  DCard,
  DBtn,
  DModal,
  DTextField,
  DTextAreaField,
  DSelectFieldSearch,
  DPagination,
  CheckIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  MinusIcon,
  Users01Icon,
  Download01Icon,
  Lock01Icon,
  InfoCircleIcon,
  File04Icon,
  PackageIcon,
  Loading02Icon,
  AlertCircleIcon,
  Mail01Icon,
  FileDownload01Icon,
  EyeIcon
} from 'front-library'
import ProcessedPayrollCardItem from '../components/ProcessedPayrollCard.vue'
import {
  getProcessedPayrolls,
  getProcessTypes,
  getEmployees,
  createExport,
  getRecentExports,
  getExportDownloadUrl,
  sendShareEmails,
  getShareNotifications,
  downloadShareNotificationsExcel,
  ProcessedPayrollCard,
  ProcessTypeOption,
  ExportEmployee,
  ExportFormat,
  ExportBackendFormat,
  RecentExport,
  ShareNotification,
  ShareNotificationFilters
} from '@/app/core/services/payment-slips-export'

usePayrollChange(() => window.location.reload())

const notifications = useNotificationsStore()
const payrollStore = usePayrollStore()
const userStore = useUserStore()
const { t, te } = useI18n()

/**
 * El backend guarda un código de motivo, no un texto: aquí se traduce.
 * Un código que agreguen más adelante cae al mensaje genérico en vez de
 * mostrarse crudo al usuario.
 */
const deliveryErrorText = (code: string): string => {
  const key = `paymentSlipsExport.deliveryErrors.${code}`
  return te(key) ? t(key) : t('paymentSlipsExport.deliveryErrors.UNKNOWN')
}

/* Mapea el formato de la UI al formato que espera el backend */
const BACKEND_FORMAT: Record<ExportFormat, ExportBackendFormat> = {
  PDF: 'MERGED_PDF',
  ZIP: 'ZIP'
}

/* Cuantas nominas se cargan por pagina ("Ver mas") */
const PAYROLLS_PAGE_SIZE = 10

/* Tamano aproximado por recibo (KB) para estimar el peso del export */
const PDF_KB_PER_RECEIPT = 45
const ZIP_KB_PER_RECEIPT = 30

/* Loading granular por seccion / accion (reemplaza el loader global) */
const loadingPayrolls = ref(false)
const loadingEmployees = ref(false)
const loadingRecent = ref(false)
const creatingExport = ref(false)
const downloadingId = ref('')
const sendingEmails = ref(false)
const generatingExcel = ref(false)

/* Modal */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

/* Nominas procesadas */
const payrolls = ref<ProcessedPayrollCard[]>([])
const selectedPayrollId = ref<string>('')

/* Nomina procesada seleccionada (su id es el closedPayrollId para exportar) */
const selectedPayroll = computed(() =>
  payrolls.value.find(p => p.id === selectedPayrollId.value)
)

/* Filtros de nominas */
const processTypes = ref<ProcessTypeOption[]>([])
const searchPayroll = ref('')
const processFilter = ref('')
const searchProcessType = ref('')
const visiblePayrollsCount = ref(PAYROLLS_PAGE_SIZE)

/* Empleados / recibos de pago (payment slips) */
const employees = ref<ExportEmployee[]>([])
/* Ids de los recibos de pago (payment slips) seleccionados (Set para lookup O(1)) */
const selectedSlipIds = ref<Set<string>>(new Set())
const showEmployeesPanel = ref(false)
const searchEmployee = ref('')
/* Ref al contenedor scrolleable de la lista virtualizada de empleados */
const employeesListRef = ref<HTMLElement | null>(null)

/* Dropdown de exportar */
const showExportMenu = ref(false)

/* Exportaciones recientes */
const recentExports = ref<RecentExport[]>([])
const refresh = ref()

/* Cada cuanto se refresca la card de actividad (la pestana visible) */
const ACTIVITY_REFRESH_MS = 5000

/* Solo se muestran las ultimas N exportaciones recientes */
const RECENT_EXPORTS_LIMIT = 10
const visibleRecentExports = computed(() =>
  recentExports.value.slice(0, RECENT_EXPORTS_LIMIT)
)

/* Timer de auto-cierre del modal de confirmacion (15s) */
const MODAL_AUTO_CLOSE_MS = 15000
const modalAutoClose = ref()
const modalPercent = ref(100)

/* Vista activa de la card de actividad */
type ActivityView = 'exports' | 'notifications'
const activityView = ref<ActivityView>('exports')

/* Notificaciones (envios de recibo + token) de los filtros seleccionados */
const shareNotifications = ref<ShareNotification[]>([])

/* Busqueda por codigo o nombre del empleado (la filtra el backend) */
const searchNotification = ref('')

/* Espera antes de consultar, para no pedir en cada tecla */
const SEARCH_DEBOUNCE_MS = 400
let searchDebounce: ReturnType<typeof setTimeout> | null = null

/* Paginacion de notificaciones (la resuelve el backend en SQL) */
const NOTIFICATIONS_PAGE_SIZES = [10, 25, 50, 100]
const notificationsPage = ref(1)
const notificationsPageSize = ref(10)
const notificationsTotal = ref(0)

/* Rango de filas mostrado en el pie de la tabla */
const notificationsStartRow = computed(() =>
  notificationsTotal.value === 0
    ? 0
    : (notificationsPage.value - 1) * notificationsPageSize.value + 1
)
const notificationsEndRow = computed(() =>
  Math.min(
    notificationsPage.value * notificationsPageSize.value,
    notificationsTotal.value
  )
)

/* Nominas filtradas por nombre y tipo de proceso */
const filteredPayrolls = computed(() => {
  const term = searchPayroll.value.trim().toLowerCase()
  return payrolls.value.filter(p => {
    const matchesName =
      !term ||
      p.name.toLowerCase().includes(term) ||
      p.period.toLowerCase().includes(term) ||
      p.processLabel.toLowerCase().includes(term)
    const matchesProcess =
      !processFilter.value || p.processCode === processFilter.value
    return matchesName && matchesProcess
  })
})

/* Nominas visibles (paginacion incremental "Ver mas") */
const visiblePayrolls = computed(() =>
  filteredPayrolls.value.slice(0, visiblePayrollsCount.value)
)

/* Si quedan mas nominas por mostrar */
const hasMorePayrolls = computed(
  () => filteredPayrolls.value.length > visiblePayrollsCount.value
)

/* Opciones del filtro de tipo de proceso (filtradas por busqueda) */
const processTypeOptions = computed(() => {
  const term = searchProcessType.value.trim().toLowerCase()
  const all = [
    { id: '', name: t('paymentSlipsExport.filters.allProcesses') },
    ...processTypes.value
  ]
  if (!term) return all
  return all.filter(o => o.name.toLowerCase().includes(term))
})

/* Empleados filtrados por busqueda (panel inline) */
const filteredEmployees = computed(() => {
  const term = searchEmployee.value.trim().toLowerCase()
  if (!term) return employees.value
  return employees.value.filter(
    e =>
      e.name.toLowerCase().includes(term) || e.code.toLowerCase().includes(term)
  )
})

/* Cantidad de recibos seleccionados */
const selectedEmployeesCount = computed(() => selectedSlipIds.value.size)

/* Todos los recibos incluidos por defecto */
const allEmployeesSelected = computed(
  () =>
    employees.value.length > 0 &&
    selectedSlipIds.value.size === employees.value.length
)

/* Seleccion parcial de recibos (algunos, pero no todos) */
const someEmployeesSelected = computed(
  () =>
    selectedSlipIds.value.size > 0 &&
    selectedSlipIds.value.size < employees.value.length
)

/* Ids de empleado de los recibos seleccionados (document-sharing filtra por empleado) */
const selectedEmployeeIds = computed(() =>
  employees.value
    .filter(employee => selectedSlipIds.value.has(employee.id))
    .map(employee => employee.employeeId)
)

/**
 * Filtros que comparten el envio de correos, la tabla de notificaciones y su Excel.
 *
 * Si estan todos los empleados seleccionados se omite employeeIds para que el
 * backend resuelva la nomina completa y no viaje una query string enorme.
 */
const shareFilters = computed<ShareNotificationFilters>(() => ({
  closedPayrollIds: selectedPayrollId.value ? [selectedPayrollId.value] : [],
  employeeIds: allEmployeesSelected.value
    ? undefined
    : selectedEmployeeIds.value
}))

/**
 * Filtros de la tabla de notificaciones: los de arriba mas la busqueda.
 *
 * Solo los usa la tabla. El envio de correos y el Excel usan shareFilters, para
 * que buscar no reduzca a quien se le manda el recibo ni lo que se exporta.
 */
const notificationFilters = computed<ShareNotificationFilters>(() => ({
  ...shareFilters.value,
  search: searchNotification.value
}))

/* -------------------------------------------------------------------------- */
/* Virtualizacion de la lista de empleados                                     */
/* Solo se renderizan las filas visibles (+buffer). Requiere altura fija.      */
/* -------------------------------------------------------------------------- */

/* Altura fija de cada fila en px (debe coincidir con el height del template) */
const ROW_HEIGHT = 41
/* Alto visible del contenedor scrolleable (coincide con max-height del panel) */
const VIEWPORT_HEIGHT = 420
/* Filas extra renderizadas arriba/abajo del viewport para un scroll suave */
const ROW_BUFFER = 6

/* Scroll vertical actual del contenedor de empleados */
const employeesScrollTop = ref(0)

/* Alto total del listado completo (para el spacer que habilita el scroll real) */
const employeesTotalHeight = computed(
  () => filteredEmployees.value.length * ROW_HEIGHT
)

/* Indice de la primera fila a renderizar (con buffer) */
const employeesStartIndex = computed(() =>
  Math.max(0, Math.floor(employeesScrollTop.value / ROW_HEIGHT) - ROW_BUFFER)
)

/* Indice (exclusivo) de la ultima fila a renderizar (con buffer) */
const employeesEndIndex = computed(() =>
  Math.min(
    filteredEmployees.value.length,
    Math.ceil((employeesScrollTop.value + VIEWPORT_HEIGHT) / ROW_HEIGHT) +
      ROW_BUFFER
  )
)

/* Subconjunto de empleados realmente montado en el DOM */
const visibleEmployees = computed(() =>
  filteredEmployees.value.slice(
    employeesStartIndex.value,
    employeesEndIndex.value
  )
)

/* Desplazamiento del bloque visible desde el tope del listado */
const employeesOffsetY = computed(() => employeesStartIndex.value * ROW_HEIGHT)

/* Handler de scroll del contenedor de empleados */
const onEmployeesScroll = (event: Event) => {
  employeesScrollTop.value = (event.target as HTMLElement).scrollTop
}

/* Reinicia el scroll de la lista (al abrir panel, cambiar nomina o buscar) */
const resetEmployeesScroll = () => {
  employeesScrollTop.value = 0
  if (employeesListRef.value) employeesListRef.value.scrollTop = 0
}

/* Al filtrar por busqueda, la lista cambia: volver el scroll al inicio */
watch(searchEmployee, () => resetEmployeesScroll())

/* Abre el panel de seleccion y reinicia el scroll de la lista virtualizada */
const openEmployeesPanel = () => {
  showEmployeesPanel.value = true
  void nextTick(() => resetEmployeesScroll())
}

/* Formatea un tamano en KB a texto legible (KB / MB) */
const formatSize = (kb: number): string => {
  if (kb <= 0) return '—'
  if (kb < 1024) return `~${Math.round(kb)} KB`
  return `~${(kb / 1024).toFixed(1)} MB`
}

/* Formatea un tamano real en bytes a KB o MB segun su peso */
const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return '—'
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

/* Tamano estimado del export segun empleados seleccionados y formato */
const estimatedSize = (format: ExportFormat): string => {
  const count = selectedEmployeesCount.value
  if (count === 0) return '—'
  const kbPer = format === 'PDF' ? PDF_KB_PER_RECEIPT : ZIP_KB_PER_RECEIPT
  return formatSize(count * kbPer)
}

/* Reinicia la paginacion cuando cambian los filtros */
const resetPayrollsPagination = () => {
  visiblePayrollsCount.value = PAYROLLS_PAGE_SIZE
}

/* Mostrar mas nominas */
const showMorePayrolls = () => {
  visiblePayrollsCount.value += PAYROLLS_PAGE_SIZE
}

/* Seleccionar una nomina */
const selectPayroll = async (id: string) => {
  if (selectedPayrollId.value === id) return
  selectedPayrollId.value = id
  showEmployeesPanel.value = false
  const payroll = payrolls.value.find(p => p.id === id)
  if (payroll) await loadEmployees(payroll.id)
}

/* Cargar recibos de pago de la nomina y seleccionarlos todos por defecto */
const loadEmployees = async (closedPayrollId: string) => {
  loadingEmployees.value = true
  try {
    employees.value = await getEmployees(closedPayrollId)
    selectedSlipIds.value = new Set(employees.value.map(e => e.id))
    resetEmployeesScroll()
  } catch (error: any) {
    showError(error)
  } finally {
    loadingEmployees.value = false
  }
}

/* Saber si un recibo esta seleccionado (O(1) sobre el Set) */
const isEmployeeSelected = (id: string) => selectedSlipIds.value.has(id)

/* Alternar seleccion de un recibo (reasigna el Set para disparar reactividad) */
const toggleEmployee = (id: string) => {
  const next = new Set(selectedSlipIds.value)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  selectedSlipIds.value = next
}

/* Seleccionar / quitar todos los recibos */
const toggleAllEmployees = () => {
  if (allEmployeesSelected.value) {
    selectedSlipIds.value = new Set()
  } else {
    selectedSlipIds.value = new Set(employees.value.map(e => e.id))
  }
}

/* Ejecutar exportacion */
const exportSlips = async (format: ExportFormat) => {
  showExportMenu.value = false
  if (!selectedPayrollId.value) {
    notifications.addNotification({
      title: t('paymentSlipsExport.notification.noPayrollTitle'),
      text: t('paymentSlipsExport.notification.noPayrollText'),
      type: 'warning',
      time: 8000
    })
    return
  }
  if (selectedSlipIds.value.size === 0) {
    notifications.addNotification({
      title: t('paymentSlipsExport.notification.noEmployeesTitle'),
      text: t('paymentSlipsExport.notification.noEmployeesText'),
      type: 'warning',
      time: 8000
    })
    return
  }
  creatingExport.value = true
  try {
    await createExport({
      paymentSlipIds: Array.from(selectedSlipIds.value),
      displayName: selectedPayroll.value?.name ?? '',
      format: BACKEND_FORMAT[format],
      requestedBy: userStore.user?.id ?? ''
    })
    typeAlertModal.value = 'success'
    titleModal.value = t('paymentSlipsExport.modal.queuedTitle')
    descriptionModal.value = t('paymentSlipsExport.modal.queuedDescription', {
      format
    })
    openConfirmationModal()
    await loadRecentExports()
  } catch (error: any) {
    showError(error)
  } finally {
    creatingExport.value = false
  }
}

/* Cargar exportaciones recientes */
const loadRecentExports = async () => {
  try {
    recentExports.value = await getRecentExports()
  } catch (error: any) {
    showError(error)
  } finally {
    /* Apaga el loader de la carga inicial; en los refrescos periodicos
     * loadingRecent ya esta en false, asi que no parpadea. */
    loadingRecent.value = false
  }
}

/* Valida que haya nomina y recibos seleccionados antes de enviar o exportar */
const hasValidSelection = (): boolean => {
  if (!selectedPayrollId.value) {
    notifications.addNotification({
      title: t('paymentSlipsExport.notification.noPayrollTitle'),
      text: t('paymentSlipsExport.notification.noPayrollText'),
      type: 'warning',
      time: 8000
    })
    return false
  }
  if (selectedSlipIds.value.size === 0) {
    notifications.addNotification({
      title: t('paymentSlipsExport.notification.noEmployeesTitle'),
      text: t('paymentSlipsExport.notification.noEmployeesText'),
      type: 'warning',
      time: 8000
    })
    return false
  }
  return true
}

/* Motivo del envio: el backend lo guarda en cada share y exige 20 caracteres */
const JUSTIFICATION_MIN_LENGTH = 20
const JUSTIFICATION_MAX_LENGTH = 500

const showJustificationModal = ref(false)
const justification = ref('')
const justificationTouched = ref(false)

const justificationLength = computed(() => justification.value.trim().length)

const justificationValid = computed(
  () =>
    justificationLength.value >= JUSTIFICATION_MIN_LENGTH &&
    justificationLength.value <= JUSTIFICATION_MAX_LENGTH
)

/* El error solo aparece cuando ya escribio: no se recibe al abrir el modal */
const justificationError = computed(() =>
  !justificationTouched.value || justificationValid.value
    ? ''
    : t('paymentSlipsExport.justification.invalid', [
        JUSTIFICATION_MIN_LENGTH,
        JUSTIFICATION_MAX_LENGTH
      ])
)

/* Pedir el motivo antes de enviar nada */
const askForJustification = () => {
  if (!hasValidSelection()) return

  justification.value = ''
  justificationTouched.value = false
  showJustificationModal.value = true
}

const closeJustificationModal = () => {
  showJustificationModal.value = false
}

/* Encolar el envio de correos con el recibo y su enlace magico */
const sendEmails = async () => {
  if (!justificationValid.value) {
    justificationTouched.value = true
    return
  }

  showJustificationModal.value = false
  sendingEmails.value = true
  try {
    const result = await sendShareEmails({
      ...shareFilters.value,
      justification: justification.value.trim()
    })
    typeAlertModal.value = 'success'
    titleModal.value = t('paymentSlipsExport.modal.emailsQueuedTitle')
    descriptionModal.value = t(
      'paymentSlipsExport.modal.emailsQueuedDescription',
      { count: result.requested }
    )
    openConfirmationModal()
    /* Tras enviar, el usuario espera ver el resultado en notificaciones. Los
     * envios nuevos entran primero (created_at DESC), asi que se va a la pagina 1 */
    activityView.value = 'notifications'
    await reloadShareNotificationsFromStart()
  } catch (error: any) {
    showError(error)
  } finally {
    sendingEmails.value = false
  }
}

/* Cargar la pagina actual de notificaciones */
const loadShareNotifications = async () => {
  if (!selectedPayrollId.value) {
    shareNotifications.value = []
    notificationsTotal.value = 0
    return
  }
  try {
    const page = await getShareNotifications(
      notificationFilters.value,
      notificationsPage.value,
      notificationsPageSize.value
    )
    shareNotifications.value = page.items
    notificationsTotal.value = page.totalItems
  } catch (error: any) {
    showError(error)
  }
}

/* Volver a la primera pagina y recargar (al cambiar filtros o tamano de pagina) */
const reloadShareNotificationsFromStart = async () => {
  notificationsPage.value = 1
  await loadShareNotifications()
}

/* Cambiar de pagina desde DPagination */
const changeNotificationsPage = async (page: number) => {
  notificationsPage.value = page
  await loadShareNotifications()
}

/* Cambiar filas por pagina: el rango se recalcula desde la primera pagina */
const changeNotificationsPageSize = async (pageSize: number) => {
  notificationsPageSize.value = pageSize
  await reloadShareNotificationsFromStart()
}

/* Buscar consulta al backend, asi que se espera a que el usuario deje de teclear */
const onSearchNotification = () => {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => {
    void reloadShareNotificationsFromStart()
  }, SEARCH_DEBOUNCE_MS)
}

/* Refresca solo la pestana visible: la otra no se consulta */
const refreshActivity = async () => {
  if (activityView.value === 'exports') {
    await loadRecentExports()
    return
  }
  await loadShareNotifications()
}

/* Cambiar de vista refresca la pestana destino sin esperar al siguiente ciclo */
const selectActivityView = async (view: ActivityView) => {
  if (activityView.value === view) return
  activityView.value = view
  await refreshActivity()
}

/**
 * Descargar el Excel de la nomina y los empleados del filtro superior.
 *
 * Es independiente de la tabla: ignora la busqueda y la paginacion, asi que
 * siempre exporta todo lo seleccionado arriba.
 */
const generateNotificationsExcel = async () => {
  if (!hasValidSelection()) return

  generatingExcel.value = true
  try {
    await downloadShareNotificationsExcel(
      shareFilters.value,
      payrollStore.payroll?.code ?? '',
      selectedPayroll.value?.endDate ?? ''
    )
  } catch (error: any) {
    showError(error)
  } finally {
    generatingExcel.value = false
  }
}

/* Los filtros de arriba son la base de la tabla: al cambiarlos, se recarga
 * desde la primera pagina, porque la pagina actual puede quedar fuera de rango */
watch([selectedPayrollId, selectedSlipIds], () => {
  if (activityView.value === 'notifications') {
    void reloadShareNotificationsFromStart()
  }
})

/* Descargar una exportacion: pide la URL prefirmada de S3 y la abre */
const download = async (item: RecentExport) => {
  downloadingId.value = item.id
  try {
    const url = await getExportDownloadUrl(item.id)
    if (!url) return
    /* URL prefirmada de S3 apuntando a un archivo: descarga en la misma pestana */
    window.location.href = url
  } catch (error: any) {
    showError(error)
  } finally {
    downloadingId.value = ''
  }
}

/* Abrir modal de confirmacion con auto-cierre y barra de progreso (15s) */
const openConfirmationModal = () => {
  clearInterval(modalAutoClose.value)
  showModal.value = true
  modalPercent.value = 100
  const steps = MODAL_AUTO_CLOSE_MS / 10
  let actual = steps
  modalAutoClose.value = setInterval(() => {
    actual -= 1
    modalPercent.value = (100 * actual) / steps
    if (modalPercent.value <= 0) {
      clearInterval(modalAutoClose.value)
      showModal.value = false
    }
  }, 10)
}

/* Cerrar modal */
const closeModal = () => {
  clearInterval(modalAutoClose.value)
  showModal.value = false
}

/* Mostrar notificacion de error */
const showError = (error: any) => {
  notifications.addNotification({
    title: 'Error',
    text: error?.message ?? String(error),
    type: 'error',
    time: 10000
  })
}

/* Carga nominas procesadas + recibos de la mas reciente (list payment slips) */
const loadPayrollsAndEmployees = async () => {
  loadingPayrolls.value = true
  try {
    processTypes.value = await getProcessTypes()
    payrolls.value = await getProcessedPayrolls()
    /* Las nominas ya estan: liberamos su loader antes de cargar empleados */
    loadingPayrolls.value = false
    const mostRecent =
      payrolls.value.find(p => p.isMostRecent) ?? payrolls.value[0]
    if (mostRecent) {
      selectedPayrollId.value = mostRecent.id
      await loadEmployees(mostRecent.id)
    }
  } catch (error: any) {
    showError(error)
  } finally {
    loadingPayrolls.value = false
  }
}

onMounted(() => {
  loadingRecent.value = true
  /* Las exportaciones recientes (list exports) no dependen de las nominas ni
   * de los recibos (list payment slips): se cargan en paralelo para no
   * esperar a esa cadena. */
  void loadPayrollsAndEmployees()
  void loadRecentExports()
  /* Un solo timer para la card de actividad: refresca la pestana seleccionada */
  refresh.value = setInterval(() => {
    void refreshActivity()
  }, ACTIVITY_REFRESH_MS)
})

onUnmounted(() => {
  clearInterval(refresh.value)
  clearInterval(modalAutoClose.value)
  if (searchDebounce) clearTimeout(searchDebounce)
})
</script>
<template>
  <div
    class="h-full px-5 pb-5 overflow-auto scroll"
    @click="showExportMenu = false">
    <DModal
      :open="showJustificationModal"
      typeAlert="question"
      :title="t('paymentSlipsExport.justification.title')"
      :description="t('paymentSlipsExport.justification.description')"
      :acceptQuestion="t('paymentSlipsExport.justification.confirm')"
      @closeModal="closeJustificationModal"
      @otherMethod="sendEmails">
      <template #form>
        <DTextAreaField
          v-model="justification"
          class="pt-3 pb-1.5 w-9/12"
          :label="t('paymentSlipsExport.justification.label')"
          :resize="false"
          :required="true"
          :rows="5"
          :error="justificationError"
          @input="justificationTouched = true" />
        <p class="w-9/12 text-xs text-gray-500 text-right">
          {{ justificationLength }} / {{ JUSTIFICATION_MAX_LENGTH }}
        </p>
      </template>
    </DModal>
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template #actions>
        <footer
          class="relative sticky inset-x-0 bottom-0 bg-gray-100 w-full flex justify-center items-center sm:justify-end h-16">
          <div class="absolute top-0 w-full bg-gray-200 h-1">
            <div
              class="h-1 bg-lime-500"
              :style="{ width: `${modalPercent}%` }" />
          </div>
          <DBtn
            type="button"
            color="secondary"
            class="mx-5"
            @click="closeModal">
            {{ t('form.button.close') }}
          </DBtn>
        </footer>
      </template>
    </DModal>

    <!-- Header -->
    <div class="bg-gray-50 pt-responsive pb-6">
      <p
        class="text-3xl font-semibold text-shadow text-gray-900"
        v-html="t('paymentSlipsExport.title')" />
      <p
        class="text-gray-500 text-shadow mt-1"
        v-html="t('paymentSlipsExport.description')" />
    </div>

    <!-- Card unificada: Nominas procesadas + Empleados -->
    <DCard>
      <div class="grid grid-cols-1 lg:grid-cols-2">
        <!-- Columna izquierda: Nominas procesadas -->
        <div class="px-3 py-2 lg:border-r lg:border-gray-100 lg:pr-6">
          <h3
            class="text-lg font-semibold text-gray-900 mb-4"
            v-html="t('paymentSlipsExport.processedPayrolls.title')" />

          <!-- Filtros -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <DTextField
              :placeholder="t('paymentSlipsExport.filters.searchName') + '...'"
              :icon="'SearchLgIcon'"
              v-model="searchPayroll"
              @input="resetPayrollsPagination" />
            <DSelectFieldSearch
              :placeholder="t('paymentSlipsExport.filters.processType')"
              v-model="processFilter"
              :options="processTypeOptions"
              @filter="searchProcessType = $event"
              @change="resetPayrollsPagination" />
          </div>

          <!-- Lista de nominas (scrolleable) -->
          <div
            class="flex flex-col gap-3 overflow-y-auto scroll pr-1"
            style="max-height: 420px">
            <!-- Loader mientras se cargan las nominas -->
            <div
              v-if="loadingPayrolls"
              class="flex flex-col items-center justify-center py-10 text-gray-400 gap-2">
              <Loading02Icon class="w-6 h-6 text-blue-500 rotate" />
              <span class="text-sm">
                {{ t('paymentSlipsExport.loading.data') }}
              </span>
            </div>
            <template v-else>
              <ProcessedPayrollCardItem
                v-for="payroll in visiblePayrolls"
                :key="payroll.id"
                :payroll="payroll"
                :selected="payroll.id === selectedPayrollId"
                @select="selectPayroll" />
              <div
                v-if="filteredPayrolls.length === 0"
                class="py-8 text-center text-sm text-gray-400">
                {{ t('paymentSlipsExport.processedPayrolls.noResults') }}
              </div>

              <!-- Ver mas (carga de a 10 dentro del contenedor) -->
              <div v-if="hasMorePayrolls" class="flex justify-center pt-1 pb-2">
                <button
                  type="button"
                  class="text-blue-600 font-semibold hover:text-blue-700 text-sm flex items-center gap-1"
                  @click="showMorePayrolls">
                  {{ t('paymentSlipsExport.processedPayrolls.showMore') }}
                  <ChevronDownIcon class="w-4 h-4" />
                </button>
              </div>
            </template>
          </div>

          <div class="flex items-center gap-2 text-sm text-gray-400 mt-5">
            <Lock01Icon class="w-4 h-4" />
            <span v-html="t('paymentSlipsExport.processedPayrolls.onlyOne')" />
          </div>
        </div>

        <!-- Columna derecha: Seleccion de empleados -->
        <div class="px-3 py-2 lg:pl-6 flex flex-col">
          <!-- Loader mientras se cargan los recibos de la nomina -->
          <div
            v-if="loadingEmployees"
            class="flex flex-col items-center justify-center flex-1 py-10 text-gray-400 gap-2">
            <Loading02Icon class="w-6 h-6 text-blue-500 rotate" />
            <span class="text-sm">
              {{ t('paymentSlipsExport.loading.employees') }}
            </span>
          </div>
          <!-- Resumen -->
          <div
            v-else-if="!showEmployeesPanel"
            class="flex flex-col items-center justify-center text-center flex-1 py-6">
            <div
              class="flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mb-5">
              <CheckCircleIcon class="w-9 h-9 text-green-500" />
            </div>
            <h2 class="text-2xl font-semibold text-gray-900">
              {{
                t('paymentSlipsExport.selection.selected', {
                  count: selectedEmployeesCount
                })
              }}
            </h2>
            <p class="text-gray-500 mt-2 max-w-md">
              {{ t('paymentSlipsExport.selection.defaultAll') }}
            </p>
            <DBtn
              type="button"
              color="secondary"
              class="mt-6 px-5 py-2.5 font-medium flex items-center gap-2"
              :disabled="!selectedPayrollId"
              @click.prevent="openEmployeesPanel">
              <Users01Icon class="w-5 h-5" />
              {{ t('paymentSlipsExport.selection.selectEmployees') }}
            </DBtn>
            <p class="text-sm text-gray-400 mt-3 max-w-sm">
              {{ t('paymentSlipsExport.selection.useOnlyIf') }}
            </p>
          </div>

          <!-- Panel de seleccion inline -->
          <div v-else class="flex flex-col flex-1 min-h-0">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-lg font-semibold text-gray-900">
                {{ t('paymentSlipsExport.selection.selectEmployees') }}
              </h3>
              <button
                type="button"
                class="text-sm text-blue-600 font-semibold hover:text-blue-700"
                @click="showEmployeesPanel = false">
                {{ t('paymentSlipsExport.selection.backToSummary') }}
              </button>
            </div>
            <div class="mb-3">
              <DTextField
                class="w-full"
                :placeholder="
                  t('paymentSlipsExport.selection.searchEmployee') + '...'
                "
                :icon="'SearchLgIcon'"
                v-model="searchEmployee" />
            </div>
            <!-- Check tri-estado + contador (seleccionar/quitar todos) -->
            <div
              class="flex items-center gap-2 mb-2 cursor-pointer select-none w-fit"
              @click="toggleAllEmployees">
              <span
                class="flex items-center justify-center w-5 h-5 rounded border transition-colors shrink-0"
                :class="
                  allEmployeesSelected || someEmployeesSelected
                    ? 'bg-blue-600 border-blue-600'
                    : 'border-gray-300 bg-white'
                ">
                <CheckIcon
                  v-if="allEmployeesSelected"
                  class="w-4 h-4 text-white" />
                <MinusIcon
                  v-else-if="someEmployeesSelected"
                  class="w-4 h-4 text-white" />
              </span>
              <span class="text-sm text-gray-500">
                {{
                  t('paymentSlipsExport.selection.selectedCount', {
                    selected: selectedEmployeesCount,
                    total: employees.length
                  })
                }}
              </span>
            </div>
            <div
              ref="employeesListRef"
              class="border border-gray-100 rounded-lg overflow-y-auto scroll flex-1"
              style="max-height: 420px"
              @scroll="onEmployeesScroll">
              <!-- Empty state -->
              <div
                v-if="filteredEmployees.length === 0"
                class="py-8 text-center text-sm text-gray-400">
                {{ t('paymentSlipsExport.selection.noResults') }}
              </div>
              <!-- Lista virtualizada: spacer del alto total + solo filas visibles -->
              <div
                v-else
                class="relative"
                :style="{ height: employeesTotalHeight + 'px' }">
                <div
                  class="absolute inset-x-0 top-0"
                  :style="{ transform: `translateY(${employeesOffsetY}px)` }">
                  <div
                    v-for="employee in visibleEmployees"
                    :key="employee.id"
                    class="flex items-center gap-3 px-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                    :style="{ height: ROW_HEIGHT + 'px' }"
                    @click="toggleEmployee(employee.id)">
                    <span
                      class="flex items-center justify-center w-5 h-5 rounded border transition-colors shrink-0"
                      :class="
                        isEmployeeSelected(employee.id)
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300 bg-white'
                      ">
                      <CheckIcon
                        v-if="isEmployeeSelected(employee.id)"
                        class="w-4 h-4 text-white" />
                    </span>
                    <div class="flex items-baseline gap-2 min-w-0">
                      <span class="text-xs text-gray-400 shrink-0">
                        {{ employee.code }}
                      </span>
                      <span class="text-sm font-medium text-gray-900 truncate">
                        {{ employee.name }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer con boton Exportar en esquina inferior derecha -->
      <div
        class="flex items-center justify-between gap-3 border-t border-gray-100 mt-4 pt-4 px-3">
        <div class="flex items-start gap-2 text-xs text-gray-400 max-w-md">
          <InfoCircleIcon class="w-4 h-4 shrink-0 mt-0.5" />
          <span>{{ t('paymentSlipsExport.export.backgroundNote') }}</span>
        </div>
        <div class="flex items-center gap-3">
          <!-- Enviar correos (usa los mismos filtros de nomina y empleados) -->
          <DBtn
            type="button"
            color="secondary"
            class="px-6 py-3 font-semibold flex items-center gap-2"
            :disabled="!selectedPayrollId"
            @click.prevent="askForJustification">
            <Mail01Icon class="w-5 h-5" />
            {{ t('paymentSlipsExport.emails.button') }}
          </DBtn>

          <!-- Boton split Exportar -->
          <div class="relative" @click.stop>
            <div class="flex">
              <DBtn
                type="button"
                class="rounded-r-none px-6 py-3 font-semibold flex items-center gap-2"
                :loading="creatingExport"
                :disabled="creatingExport"
                @click.prevent="exportSlips('PDF')">
                <Download01Icon class="w-5 h-5" />
                {{ t('paymentSlipsExport.export.buttonPdf') }}
              </DBtn>
              <button
                type="button"
                :disabled="creatingExport"
                class="bg-blue-600 hover:bg-blue-700 text-white rounded-r-lg px-3 border-l border-blue-500 flex items-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                @click.prevent="showExportMenu = !showExportMenu">
                <ChevronDownIcon class="w-5 h-5" />
              </button>
            </div>

            <!-- Menu desplegable de formato (se abre hacia arriba) -->
            <div
              v-if="showExportMenu"
              class="absolute right-0 bottom-full mb-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 z-20 overflow-hidden text-left">
              <button
                type="button"
                class="w-full flex gap-3 px-4 py-4 hover:bg-gray-50 transition-colors text-left"
                @click.prevent="exportSlips('PDF')">
                <File04Icon class="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
                <div>
                  <div class="flex items-center gap-2">
                    <span class="font-semibold text-gray-900">
                      {{ t('paymentSlipsExport.export.pdf') }}
                    </span>
                    <span
                      class="text-xs font-medium text-blue-600 bg-blue-100 rounded-md px-2 py-0.5">
                      {{ t('paymentSlipsExport.export.recommended') }}
                    </span>
                  </div>
                  <p class="text-sm text-gray-500 mt-0.5">
                    {{ t('paymentSlipsExport.export.pdfDesc') }}
                  </p>
                  <p
                    v-if="selectedEmployeesCount > 0"
                    class="text-xs text-gray-400 mt-1">
                    {{
                      t('paymentSlipsExport.export.estimatedSize', {
                        size: estimatedSize('PDF')
                      })
                    }}
                  </p>
                </div>
              </button>
              <div class="border-t border-gray-100" />
              <button
                type="button"
                class="w-full flex gap-3 px-4 py-4 hover:bg-gray-50 transition-colors text-left"
                @click.prevent="exportSlips('ZIP')">
                <PackageIcon class="w-6 h-6 text-indigo-500 shrink-0 mt-0.5" />
                <div>
                  <span class="font-semibold text-gray-900">
                    {{ t('paymentSlipsExport.export.zip') }}
                  </span>
                  <p class="text-sm text-gray-500 mt-0.5">
                    {{ t('paymentSlipsExport.export.zipDesc') }}
                  </p>
                  <p
                    v-if="selectedEmployeesCount > 0"
                    class="text-xs text-gray-400 mt-1">
                    {{
                      t('paymentSlipsExport.export.estimatedSize', {
                        size: estimatedSize('ZIP')
                      })
                    }}
                  </p>
                </div>
              </button>
              <div
                class="flex items-start gap-2 px-4 py-3 bg-gray-50 border-t border-gray-100">
                <InfoCircleIcon class="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                <p class="text-xs text-gray-500">
                  {{ t('paymentSlipsExport.export.backgroundNote') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DCard>

    <!-- Actividad: exportaciones y notificaciones -->
    <DCard class="mt-5">
      <div class="px-3 py-2">
        <!-- Switch de vista (mismos estilos de pestana que DTabs) -->
        <ul class="flex flex-row overflow-auto scroll">
          <li
            class="text-base font-semibold tracking-normal p-3 text-center cursor-pointer whitespace-nowrap"
            :class="
              activityView === 'exports'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-400 border-b-2 border-gray-200'
            "
            @click="selectActivityView('exports')">
            {{ t('paymentSlipsExport.activity.exports') }}
            ({{ recentExports.length }})
          </li>
          <li
            class="text-base font-semibold tracking-normal p-3 text-center cursor-pointer whitespace-nowrap"
            :class="
              activityView === 'notifications'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-400 border-b-2 border-gray-200'
            "
            @click="selectActivityView('notifications')">
            {{ t('paymentSlipsExport.activity.notifications') }}
            ({{ notificationsTotal }})
          </li>
          <!-- Continua la linea inferior hasta el final del ancho disponible -->
          <li class="flex-1 border-b-2 border-gray-200" />
        </ul>

        <!-- Vista: exportaciones recientes -->
        <template v-if="activityView === 'exports'">
          <h3
            class="text-lg font-semibold text-gray-900 mt-4"
            v-html="t('paymentSlipsExport.recent.title')" />
          <p
            class="text-sm text-gray-500 mb-4 mt-1"
            v-html="
              t('paymentSlipsExport.recent.subtitle', {
                count: RECENT_EXPORTS_LIMIT
              })
            " />

          <div class="overflow-x-auto scroll">
            <table class="w-full text-sm">
              <tbody>
                <!-- Loader de la carga inicial de exportaciones recientes -->
                <tr v-if="loadingRecent">
                  <td colspan="7" class="py-8 text-center text-gray-400">
                    <span class="inline-flex items-center gap-2">
                      <Loading02Icon class="w-5 h-5 text-blue-500 rotate" />
                      {{ t('paymentSlipsExport.loading.recent') }}
                    </span>
                  </td>
                </tr>
                <template v-else>
                  <tr
                    v-for="item in visibleRecentExports"
                    :key="item.id"
                    class="border-t border-gray-100">
                    <!-- Fecha + estado icono -->
                    <td class="py-4 pr-3 whitespace-nowrap">
                      <div class="flex items-center gap-3">
                        <span
                          class="flex items-center justify-center w-8 h-8 rounded-full shrink-0"
                          :class="{
                            'bg-green-50': item.status === 'COMPLETED',
                            'bg-blue-50': item.status === 'PROCESSING',
                            'bg-red-50': item.status === 'ERROR'
                          }">
                          <CheckIcon
                            v-if="item.status === 'COMPLETED'"
                            class="w-4 h-4 text-green-500" />
                          <Loading02Icon
                            v-else-if="item.status === 'PROCESSING'"
                            class="w-4 h-4 text-blue-500 rotate" />
                          <AlertCircleIcon
                            v-else
                            class="w-4 h-4 text-red-500" />
                        </span>
                        <span class="text-gray-600">{{ item.createdAt }}</span>
                      </div>
                    </td>
                    <!-- Nomina -->
                    <td class="py-4 px-3 text-gray-700 whitespace-nowrap">
                      {{ item.payrollName }}
                    </td>
                    <!-- Empleados -->
                    <td class="py-4 px-3 whitespace-nowrap">
                      <div class="flex flex-col">
                        <span class="text-gray-900 font-medium">
                          {{ item.employeesCount }}
                        </span>
                        <span class="text-xs text-gray-400">
                          {{ t('paymentSlipsExport.recent.employees') }}
                        </span>
                      </div>
                    </td>
                    <!-- Estado -->
                    <td class="py-4 px-3 min-w-[180px]">
                      <div v-if="item.status === 'COMPLETED'">
                        <p class="text-green-600 font-medium">
                          {{ t('paymentSlipsExport.recent.completed') }}
                        </p>
                        <p class="text-xs text-gray-400">
                          {{ item.statusLabel }}
                        </p>
                      </div>
                      <div v-else-if="item.status === 'PROCESSING'">
                        <div class="flex items-center gap-2">
                          <Loading02Icon class="w-4 h-4 text-blue-500 rotate" />
                          <p class="text-blue-600 font-medium">
                            {{ t('paymentSlipsExport.recent.processing') }}
                          </p>
                        </div>
                      </div>
                      <div v-else>
                        <p class="text-red-600 font-medium">
                          {{ t('paymentSlipsExport.recent.error') }}
                        </p>
                        <p class="text-xs text-gray-400">
                          {{ item.statusLabel }}
                        </p>
                      </div>
                    </td>
                    <!-- Formato -->
                    <td class="py-4 px-3 whitespace-nowrap">
                      <div class="flex items-center gap-2">
                        <File04Icon
                          v-if="item.format === 'PDF'"
                          class="w-5 h-5 text-red-500" />
                        <PackageIcon v-else class="w-5 h-5 text-indigo-500" />
                        <span class="text-gray-700 font-medium">{{
                          item.format
                        }}</span>
                      </div>
                    </td>
                    <!-- Tamano -->
                    <td class="py-4 px-3 text-gray-500 whitespace-nowrap">
                      {{ formatBytes(item.sizeBytes) }}
                    </td>
                    <!-- Accion -->
                    <td class="py-4 pl-3 text-right whitespace-nowrap">
                      <button
                        v-if="item.status === 'COMPLETED'"
                        type="button"
                        :disabled="downloadingId === item.id"
                        class="text-blue-600 font-semibold hover:text-blue-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1"
                        @click="download(item)">
                        <Loading02Icon
                          v-if="downloadingId === item.id"
                          class="w-4 h-4 rotate" />
                        {{ t('paymentSlipsExport.recent.download') }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="recentExports.length === 0">
                    <td colspan="7" class="py-8 text-center text-gray-400">
                      {{ t('table.noData') }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </template>

        <!-- Vista: notificaciones (envios via document-sharing) -->
        <template v-else>
          <div class="flex items-end justify-between gap-3 mb-4 mt-4">
            <div>
              <h3
                class="text-lg font-semibold text-gray-900"
                v-html="t('paymentSlipsExport.notifications.title')" />
              <p
                class="text-sm text-gray-500 mt-1"
                v-html="t('paymentSlipsExport.notifications.subtitle')" />
            </div>
            <!-- Exporta la nomina y empleados del filtro superior, no la tabla -->
            <DBtn
              type="button"
              color="secondary"
              class="px-5 py-2.5 font-medium flex items-center gap-2 shrink-0"
              :loading="generatingExcel"
              :disabled="
                !selectedPayrollId ||
                selectedEmployeesCount === 0 ||
                generatingExcel
              "
              @click.prevent="generateNotificationsExcel">
              <FileDownload01Icon class="w-5 h-5" />
              {{ t('paymentSlipsExport.notifications.excel') }}
            </DBtn>
          </div>

          <!-- Busqueda por codigo o nombre (la resuelve el backend) -->
          <div class="mb-4 max-w-sm">
            <DTextField
              :placeholder="
                t('paymentSlipsExport.notifications.searchEmployee') + '...'
              "
              :icon="'SearchLgIcon'"
              v-model="searchNotification"
              @input="onSearchNotification" />
          </div>

          <div class="overflow-x-auto scroll">
            <table class="w-full text-sm">
              <thead>
                <tr>
                  <th
                    class="text-base text-gray-600 px-3 py-2 text-left whitespace-nowrap">
                    {{ t('paymentSlipsExport.notifications.employee') }}
                  </th>
                  <th
                    class="text-base text-gray-600 px-3 py-2 text-left whitespace-nowrap">
                    {{ t('paymentSlipsExport.notifications.sentAt') }}
                  </th>
                  <th
                    class="text-base text-gray-600 px-3 py-2 text-left whitespace-nowrap">
                    {{ t('paymentSlipsExport.notifications.opens') }}
                  </th>
                  <th
                    class="text-base text-gray-600 px-3 py-2 text-left whitespace-nowrap">
                    {{ t('paymentSlipsExport.notifications.lastOpenedAt') }}
                  </th>
                  <th
                    class="text-base text-gray-600 px-3 py-2 text-left whitespace-nowrap">
                    {{ t('paymentSlipsExport.notifications.approvedAt') }}
                  </th>
                  <th
                    class="text-base text-gray-600 px-3 py-2 text-left whitespace-nowrap">
                    {{ t('paymentSlipsExport.notifications.errorColumn') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in shareNotifications"
                  :key="item.id"
                  class="border-t border-gray-100">
                  <!-- Codigo - nombre completo, y debajo el correo destino -->
                  <td class="py-4 px-3">
                    <div class="flex flex-col">
                      <span class="text-gray-900 font-medium">
                        {{ item.employeeLabel }}
                      </span>
                      <span class="text-xs text-gray-400">
                        {{ item.recipientEmail }}
                      </span>
                    </div>
                  </td>
                  <td class="py-4 px-3 text-gray-600 whitespace-nowrap">
                    {{ item.sentAt }}
                  </td>
                  <!-- Aperturas: veces que se genero el PDF con el enlace magico -->
                  <td class="py-4 px-3 whitespace-nowrap">
                    <div class="flex items-center gap-2">
                      <EyeIcon class="w-4 h-4 text-gray-400" />
                      <span class="text-gray-900 font-medium">
                        {{ item.openCount }}
                      </span>
                    </div>
                  </td>
                  <td class="py-4 px-3 text-gray-600 whitespace-nowrap">
                    {{ item.lastOpenedAt }}
                  </td>
                  <td class="py-4 px-3 text-gray-600 whitespace-nowrap">
                    {{ item.approvedAt }}
                  </td>
                  <td class="py-4 px-3 min-w-[180px]">
                    <div v-if="item.error" class="flex items-start gap-2">
                      <AlertCircleIcon
                        class="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                      <span class="text-xs text-red-600">
                        {{ deliveryErrorText(item.error) }}
                      </span>
                    </div>
                    <span v-else class="text-gray-400">—</span>
                  </td>
                </tr>
                <tr v-if="shareNotifications.length === 0">
                  <td colspan="6" class="py-8 text-center text-gray-400">
                    {{
                      searchNotification.trim()
                        ? t('paymentSlipsExport.notifications.noResults')
                        : t('paymentSlipsExport.notifications.empty')
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <DPagination
            v-if="notificationsTotal > 0"
            :totalRows="notificationsTotal"
            :perPage="notificationsPageSize"
            :currentPage="notificationsPage"
            :startRow="notificationsStartRow"
            :endRow="notificationsEndRow"
            :perPageDropdown="NOTIFICATIONS_PAGE_SIZES"
            @currentPage="changeNotificationsPage"
            @perPage="changeNotificationsPageSize" />
        </template>
      </div>
    </DCard>
  </div>
</template>
<style scoped>
.rotate {
  animation: rotate 1s linear infinite;
}
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
